import React, { useState, useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Typography } from '@mui/material'

import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import Loading from './Loading'
import { HardwareTestResultModel } from 'src/Libs/installation-details/models'
import SelectTest from './SelectTest'
import { AvailableTest } from 'src/Libs/installation-details/dto/hardware-test-result.dto'
import TestParametersForm from './TestParametersForm'
import { TriggerHardwareTestParamsData } from '../../../../models'
import { HardwareTestResult } from './HardwareTestResult'
import ControlButtons from './ControlButtons'

const useStyles = makeStyles()(theme => ({
  paper: {
    padding: 0,
  },
  header: {
    padding: `0 ${theme.spacing(2)}`,
  },
  contentContainer: {
    width: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    padding: `${theme.spacing(4)} 0`,
    minHeight: '235px',
    '@media (max-width: 650px)': {
      paddingInline: theme.spacing(2),
    },
  },
}))

interface Props {
  testResultData?: HardwareTestResultModel
  onTriggerTest: (testId: string, params?: TriggerHardwareTestParamsData) => void
  finishTest: () => void
}

/** Component to display current status of hardware test */
const HardwareAnalysis: React.FC<Props> = ({ testResultData, onTriggerTest, finishTest }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const [selectedTest, setSelectedTest] = useState<AvailableTest>()
  const [shouldProvideParameters, setShouldProvideParameters] = useState(false)

  const onSelectionChange = (value: AvailableTest): void => {
    setSelectedTest(value)
  }

  const analyseTest = useCallback(
    (data?: TriggerHardwareTestParamsData): void => {
      const testId = selectedTest?.testId
      if (!testId) {
        return
      }
      onTriggerTest(testId, data)
    },
    [selectedTest, onTriggerTest]
  )

  const onSelectionApply = useCallback(() => {
    if ((selectedTest?.params.length || 0) > 0) {
      setShouldProvideParameters(true)
      return
    }

    analyseTest()
  }, [analyseTest, selectedTest])

  const renderCurrentAnalisesStep = useCallback(() => {
    const state = testResultData?.state
    const availableTests = testResultData?.availableTests

    if (!availableTests?.length) {
      if (!state) {
        return (
          <Box className={classes.contentContainer}>
            <Loading />
          </Box>
        )
      }

      return <HardwareTestResult testResultData={testResultData} />
    }

    if (shouldProvideParameters) {
      return <TestParametersForm selectedTest={selectedTest!} handleSubmit={analyseTest} setShouldProvideParameters={setShouldProvideParameters} />
    } else {
      return <SelectTest data={testResultData!} selectedTest={selectedTest} onChange={onSelectionChange} onSelectionApply={onSelectionApply} />
    }
  }, [testResultData, selectedTest, shouldProvideParameters, classes, analyseTest, onSelectionApply])

  return (
    <Paper
      title={
        <Typography variant="h2" component="span">
          {t('system-details.hardware-test.title')}
        </Typography>
      }
      className={classes.paper}
      appBarClass={classes.header}
    >
      {renderCurrentAnalisesStep()}
      <ControlButtons testResultData={testResultData} finishTest={finishTest} />
    </Paper>
  )
}

export default HardwareAnalysis
